// Helpers rond de deelbare "upload je foto's via je telefoon"-link.
//
// Een upload-sessie (collectie `upload_sessions`, doc-id = token) koppelt een
// deelbare link/QR aan een locatie. De eigenaar maakt en beheert deze; de
// publieke uploadpagina (/upload/:token) praat met de photoUpload Cloud Function
// om de sessie te resolven en geuploade foto's te koppelen.
import { collection, doc, getDocs, query, setDoc, updateDoc, where, serverTimestamp, Timestamp } from "firebase/firestore";
import { nanoid } from "nanoid";
import { auth, db, isAdmin } from "./firebase.js";

const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 dagen
const APP_ORIGIN = typeof window !== "undefined" ? window.location.origin : "https://admin.feestjegeven.nl";
const FUNCTION_URL =
  process.env.REACT_APP_PHOTO_UPLOAD_URL || "https://europe-west1-feestjegeven-nl.cloudfunctions.net/photoUpload";

export function uploadUrlForToken(token) {
  return `${APP_ORIGIN}/upload/${token}`;
}

function isSessionValid(data) {
  if (!data || data.active === false) return false;
  const expMs = data.expiresAt && data.expiresAt.toMillis ? data.expiresAt.toMillis() : 0;
  return !expMs || expMs > Date.now();
}

// Een admin beheert locaties van anderen en ziet daarom alle sessies van de
// locatie; een eigenaar mag alleen zijn eigen sessies lezen.
function sessionsQuery(venueId, user) {
  const filters = [where("venueId", "==", venueId)];
  if (!isAdmin()) filters.push(where("uid", "==", user.uid));
  return query(collection(db, "upload_sessions"), ...filters);
}

// Zoekt een geldige bestaande sessie voor deze locatie of maakt er een aan.
// Geeft { token, url } terug.
export async function getOrCreateUploadSession(venueId, venueName) {
  const user = auth.currentUser;
  if (!user) throw new Error("Niet ingelogd.");

  const snapshot = await getDocs(sessionsQuery(venueId, user));
  const existing = snapshot.docs.find((d) => isSessionValid(d.data()));
  if (existing) {
    return { token: existing.id, url: uploadUrlForToken(existing.id) };
  }

  const token = nanoid(16);
  await setDoc(doc(db, "upload_sessions", token), {
    venueId,
    venueName: venueName || "",
    uid: user.uid,
    active: true,
    source: "owner",
    createdAt: serverTimestamp(),
    expiresAt: Timestamp.fromMillis(Date.now() + SESSION_TTL_MS),
  });
  return { token, url: uploadUrlForToken(token) };
}

// Maakt de huidige sessie(s) voor een locatie ongeldig en maakt een nieuwe aan.
export async function regenerateUploadSession(venueId, venueName) {
  const user = auth.currentUser;
  if (!user) throw new Error("Niet ingelogd.");

  const snapshot = await getDocs(sessionsQuery(venueId, user));
  await Promise.all(
    snapshot.docs
      .filter((d) => d.data().active !== false)
      .map((d) => updateDoc(doc(db, "upload_sessions", d.id), { active: false })),
  );

  const token = nanoid(16);
  await setDoc(doc(db, "upload_sessions", token), {
    venueId,
    venueName: venueName || "",
    uid: user.uid,
    active: true,
    source: "owner",
    createdAt: serverTimestamp(),
    expiresAt: Timestamp.fromMillis(Date.now() + SESSION_TTL_MS),
  });
  return { token, url: uploadUrlForToken(token) };
}

// Aangeroepen vanaf de publieke uploadpagina: resolve de token naar locatie-info.
export async function resolveUploadSession(token) {
  const res = await fetch(FUNCTION_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "resolve", token }),
  });
  return res.json();
}

// Koppelt geuploade foto-ID's aan de locatie via de Cloud Function.
export async function attachUploadedPhotos(token, ids) {
  const res = await fetch(FUNCTION_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "attach", token, ids }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.error || "Koppelen van foto's mislukt.");
    err.status = res.status;
    throw err;
  }
  return res.json();
}
